import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Alert } from "antd";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

const SESSION_KEY_PREFIX = "license.banner.dismissed";

const dismissalKeyFor = (validUntil) => `${SESSION_KEY_PREFIX}:${validUntil ?? "unknown"}`;

const readDismissed = (validUntil) => {
    try {
        if (typeof window === "undefined") return false;
        return window.sessionStorage?.getItem(dismissalKeyFor(validUntil)) === "1";
    } catch (_) {
        return false;
    }
};

const formatValidUntil = (validUntil, language) => {
    if (!validUntil) return "";
    const d = dayjs.utc(validUntil);
    if (!d.isValid()) return String(validUntil);
    const fmt = (language || "").toLowerCase().startsWith("de") ? "DD.MM.YYYY" : "MMM D, YYYY";
    return d.format(fmt);
};

// Site-wide CSS variable consumed by Layout.module.css so the navbar (fixed,
// z-index 1040) and the main content area shift down by the banner's measured
// height while it's visible. Cleared when the banner is hidden so other layouts
// (login/registration, embedded views) don't get a phantom offset.
const BANNER_HEIGHT_VAR = "--license-banner-height";

const clearBannerHeightVar = () => {
    if (typeof document === "undefined") return;
    document.documentElement.style.removeProperty(BANNER_HEIGHT_VAR);
};

const LicenseExpiredBanner = () => {
    const { t, i18n } = useTranslation();
    const status = useSelector((state) => state.license?.status);

    const validUntil = status?.validUntil;
    const [dismissed, setDismissed] = useState(() => readDismissed(validUntil));
    const wrapperRef = useRef(null);

    useEffect(() => {
        setDismissed(readDismissed(validUntil));
    }, [validUntil]);

    const isExpired = status?.status === "Expired";
    const isMissing = status?.status === "NoLicense";
    const visible = !!status && (isExpired || isMissing) && !dismissed;

    // Measure after layout so we publish the real rendered height, including
    // text wrap on narrow viewports. Re-measure on resize so the variable
    // stays correct if the banner wraps to multiple lines.
    useLayoutEffect(() => {
        if (!visible || typeof document === "undefined") {
            clearBannerHeightVar();
            return undefined;
        }
        const publish = () => {
            const h = wrapperRef.current?.offsetHeight ?? 0;
            if (h > 0) {
                document.documentElement.style.setProperty(BANNER_HEIGHT_VAR, `${h}px`);
            }
        };
        publish();
        const ro = typeof ResizeObserver !== "undefined" && wrapperRef.current
            ? new ResizeObserver(publish)
            : null;
        ro?.observe(wrapperRef.current);
        window.addEventListener("resize", publish);
        return () => {
            window.removeEventListener("resize", publish);
            ro?.disconnect();
            clearBannerHeightVar();
        };
    }, [visible]);

    if (!visible) {
        return null;
    }

    const handleClose = () => {
        try {
            window.sessionStorage?.setItem(dismissalKeyFor(validUntil), "1");
        } catch (_) {
            // sessionStorage unavailable — accept ephemeral dismissal only
        }
        setDismissed(true);
    };

    const validUntilFormatted = formatValidUntil(validUntil, i18n.language);
    // UI-only warning: surface when the system will be locked if not renewed.
    // 3-month window matches the policy the team will enforce once the
    // superadmin dashboard ships.
    const blockDateRaw = validUntil
        ? dayjs.utc(validUntil).add(3, "month").toISOString()
        : null;
    const blockDateFormatted = formatValidUntil(blockDateRaw, i18n.language);

    const messageText = isMissing
        ? t("license.banner.noLicense")
        : t("license.banner.expired", {
              validUntil: validUntilFormatted,
              blockDate: blockDateFormatted,
          });

    return (
        <div
            ref={wrapperRef}
            style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                // Above .navbarWrapper (z-index 1040 in Layout.module.css) so it
                // isn't covered by the fixed navbar on authenticated routes.
                zIndex: 1050,
            }}
        >
            <Alert
                data-testid="license-expired-banner"
                type="error"
                banner
                showIcon
                closable
                onClose={handleClose}
                message={messageText}
                style={{ width: "100%" }}
            />
        </div>
    );
};

export default LicenseExpiredBanner;
